import { useLayoutEffect, useRef, useState } from "react";

import {
  HUB_DEPART_GONE_LEAD_MS,
  introWipeMs,
} from "@story/data-centers-hub/config/intro";
import { scheduleEnterFillPins } from "@story/data-centers-hub/config/scheduleEnterFillPins";
import getBrowserInfo from "src/utils/get-browser-info";

type Args = {
  active: boolean;
  animate: boolean;
  hold: boolean;
  depart: boolean;
  /** Forward hop in progress — we are the tracked outgoing under `hold`. */
  leaving?: boolean;
  /**
   * Mount already open (session restore / soft remount). Without this, an
   * already-active chapter replays its enter wipe over the previous chapter.
   */
  resume?: boolean;
  /**
   * When false, detach video `src` (TitleCellMedia release) while the chapter
   * shell stays mounted. SlideDeck sets this from the wipe neighborhood.
   */
  mediaWarm?: boolean;
  narrow: boolean;
};

/**
 * Enter/hold/depart timing for Loudoun / Utah / Arizona.
 *
 * `settled` drops `clip-path` on the plate and side cells so share UI can
 * escape. If that flips on while the enter clip is still closed, those cells
 * paint fully opaque and the underlay headlines disappear on the first frame.
 * Settled waits out the wipe unless this chapter is already open (hold, or
 * hold → active).
 */
export function useWipeChapterStage({
  active,
  animate,
  hold,
  depart,
  leaving = false,
  resume = false,
  mediaWarm = true,
  narrow,
}: Args) {
  const startOpen = !animate || hold || resume;
  const [onStage, setOnStage] = useState(() => startOpen);
  const [open, setOpen] = useState(() => startOpen);
  const [settled, setSettled] = useState(() => startOpen);
  /** Pin open under a running enter — see `data-hub-fill` in wipe keyframes. */
  const [fillPin, setFillPin] = useState(() => startOpen);
  /** Near wipe-end: drop enter animation — see `data-hub-fill-lock`. */
  const [fillLock, setFillLock] = useState(() => startOpen);
  /**
   * Depart close finished (or nearly) — drop paint so a terminate-frame
   * snap back to `inset(0)` cannot flash the outgoing chapter (AZ→UT).
   */
  const [departGone, setDepartGone] = useState(false);
  const clipOpenRef = useRef(startOpen);
  const openRef = useRef(open);
  const settledRef = useRef(settled);
  const narrowRef = useRef(narrow);
  openRef.current = open;
  settledRef.current = settled;
  narrowRef.current = narrow;

  useLayoutEffect(() => {
    let openRaf = 0;
    let finishTimer = 0;
    let departDebounce = 0;
    let cancelFillPins: (() => void) | undefined;
    // Read narrow from a ref — do NOT list it in deps. A chrome-driven narrow
    // flip used to re-run this effect and replay the whole enter wipe.
    const wipeMs = introWipeMs(narrowRef.current);
    // Android: push `clip-path: none` (share escape) well past the wipe so it
    // cannot share a frame with the animation terminate / mute latch.
    const settlePad = getBrowserInfo()?.android ? 480 : 48;

    if (!animate) {
      setOnStage(true);
      setOpen(true);
      setSettled(true);
      setFillPin(true);
      setFillLock(true);
      setDepartGone(false);
      clipOpenRef.current = true;
      return;
    }

    if (active) {
      setOnStage(true);
      setDepartGone(false);
      // Already open under a later chapter, or a dep change during hold.
      // A stuck ref with a closed clip must still wipe — settling now is
      // what covers the outgoing headlines.
      if (clipOpenRef.current && openRef.current) {
        setOpen(true);
        setFillPin(true);
        setFillLock(true);
        if (settledRef.current) {
          setSettled(true);
        } else {
          // Past the clip ease so `clip-path: none` cannot cancel it early.
          finishTimer = window.setTimeout(() => {
            setSettled(true);
          }, wipeMs + settlePad);
        }
        return;
      }
      setSettled(false);
      setFillPin(false);
      setFillLock(false);
      setOpen(false);
      clipOpenRef.current = false;
      openRaf = requestAnimationFrame(() => {
        openRaf = requestAnimationFrame(() => {
          clipOpenRef.current = true;
          setOpen(true);
          // Enter keyframes pin underlying `closed`. Blink intermittently
          // drops the forwards fill at wipe-end → closed hole → underlay
          // flash. Early fill + near-end fill-lock (shared helper).
          cancelFillPins = scheduleEnterFillPins({
            wipeMs,
            setFillPin,
            setFillLock,
          });
          finishTimer = window.setTimeout(() => {
            setSettled(true);
          }, wipeMs + settlePad);
        });
      });
    } else if (hold) {
      setOnStage(true);
      setOpen(true);
      setSettled(true);
      setFillPin(true);
      setFillLock(true);
      setDepartGone(false);
      clipOpenRef.current = true;
    } else if (depart) {
      setOnStage(true);
      setOpen(true);
      setSettled(true);
      setFillPin(true);
      setFillLock(false);
      setDepartGone(false);
      clipOpenRef.current = true;
      departDebounce = window.setTimeout(() => {
        setSettled(false);
        setFillPin(false);
        setFillLock(false);
        openRaf = requestAnimationFrame(() => {
          openRaf = requestAnimationFrame(() => {
            clipOpenRef.current = false;
            setOpen(false);
            // Hide slightly before wipe-end so a late setTimeout cannot land
            // after Blink’s close-fill drop (full outgoing flash ~1s in).
            finishTimer = window.setTimeout(() => {
              setDepartGone(true);
              setOnStage(false);
            }, Math.max(0, wipeMs - HUB_DEPART_GONE_LEAD_MS));
          });
        });
      }, 120);
    } else {
      // Clear immediately. The hide is delayed; a cancelled timeout used to
      // leave the ref true so the next enter settled on frame one.
      clipOpenRef.current = false;
      setFillPin(false);
      setFillLock(false);
      setDepartGone(false);
      finishTimer = window.setTimeout(() => {
        setOnStage(false);
        setOpen(false);
        setSettled(false);
      }, 80);
    }

    return () => {
      cancelAnimationFrame(openRaf);
      cancelFillPins?.();
      window.clearTimeout(finishTimer);
      window.clearTimeout(departDebounce);
    };
  }, [active, animate, hold, depart]);

  // Do NOT paint from bare `active` — that is the clipped-hole downscroll flash
  // when the underlay is not yet on hold. `hold` / `depart` are sync.
  // Active enter: stay invisible until `open` flips (same beat as the clip
  // animation). Painting earlier put an empty stacking layer over the underlay
  // for 1–2 frames while clips were closed / keyframes not attached — the
  // intermittent start-of-wipe flash.
  // `departGone` drops paint at close-end so terminate cannot revive the layer.
  const painted =
    !animate ||
    hold ||
    (depart && !departGone) ||
    (onStage && (!active || open));
  // Chapter shell stays mounted. Keep `src` attached for the whole wipe
  // neighborhood (not only while on-stage) — detaching after the first hop
  // made hop 2+ cold-start at readyState 0 and flash black as clips opened.
  // Playback still only while on-stage / mid-wipe. Outside the neighborhood,
  // TitleCellMedia releases after its grace timer.
  const onDeck = animate && onStage;
  const mediaActive = Boolean(mediaWarm);
  const mediaPlayback = Boolean(
    onDeck && mediaWarm && (active || depart || (hold && leaving)),
  );
  const stillsActive = animate ? painted : active;

  return {
    open,
    settled,
    fillPin,
    fillLock,
    departGone,
    painted,
    mediaActive,
    mediaPlayback,
    stillsActive,
  };
}
